import type { CrmIntegrationType } from '@shared';

export type CrmFieldMapping = {
  crmField: string;
  variableName: string;
  speechFormat?: string;
};

export type CrmColumnConfig = {
  tableName: string;
  customerIdColumn: string;
  customerIdDigitLength: number;
  phoneMatchColumn: string;
  callerIdMatchColumn: string[];
  phoneUpdateColumns?: string[];
  fieldMappings: CrmFieldMapping[];
};

export const crmTypeLabels: Record<CrmIntegrationType, string> = {
  caspio: 'Caspio',
  platform: 'MALA Platform',
};

export const defaultCrmColumnConfig: CrmColumnConfig = {
  tableName: '',
  customerIdColumn: '',
  customerIdDigitLength: 6,
  phoneMatchColumn: '',
  callerIdMatchColumn: [],
  fieldMappings: [],
};

export function asRecord(value: unknown) {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

export function asString(value: unknown) {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function asStringArray(value: unknown) {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : undefined;
}

function asFieldMapping(value: unknown): CrmFieldMapping | null {
  const record = asRecord(value);

  if (!record) return null;

  return {
    crmField: asString(record.crmField) ?? '',
    variableName: asString(record.variableName) ?? '',
    speechFormat: asString(record.speechFormat),
  };
}

export function parseCrmColumnConfig(value: unknown): CrmColumnConfig {
  const record = asRecord(value);

  if (!record) return defaultCrmColumnConfig;

  return {
    tableName: asString(record.tableName) ?? '',
    customerIdColumn: asString(record.customerIdColumn) ?? '',
    customerIdDigitLength: typeof record.customerIdDigitLength === 'number' ? record.customerIdDigitLength : 6,
    phoneMatchColumn: asString(record.phoneMatchColumn) ?? '',
    callerIdMatchColumn: asStringArray(record.callerIdMatchColumn) ?? [],
    phoneUpdateColumns: asStringArray(record.phoneUpdateColumns),
    fieldMappings: Array.isArray(record.fieldMappings)
      ? record.fieldMappings.map(asFieldMapping).filter((m): m is CrmFieldMapping => m !== null)
      : [],
  };
}
